import React, { useRef } from "react";
import Box from "@mui/material/Box";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Tooltip from "@mui/material/Tooltip";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrButton from "src/design-system/components/GrButton";
import GrLoader from "src/design-system/components/GrLoader";
import GrChatComposer from "src/design-system/components/GrChatComposer";
import GrEmojiPicker from "src/design-system/components/GrEmojiPicker";
import { insertAtCursor } from "src/utils/insertAtCursor";
import GrAvatar from "src/design-system/components/GrAvatar";
import { grCleanName } from "src/design-system/components/GrChatListRow";
import { s3baseUrl } from "src/config/config";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";

// Write-a-comment / reply box on feed posts (dashboard card, post popup, replies), Facebook style: the
// member's photo on the left, then a soft rounded box with the text (grows with it) and emoji, photo
// and send inside on the right (under the text once the member types); a picked photo shows above the text. Ctrl/Cmd + Enter sends.
// Editing shows "Editing your comment · Cancel" under the box.

const formSx = { display: "grid", gap: gr.space[2], mt: gr.space[3] };
const rowSx = { display: "flex", alignItems: "flex-start", gap: gr.space[2] };
// Lines the photo up with the box's first line.
const avatarSx = { mt: gr.space[1], flex: "none" };
const previewSx = {
  position: "relative",
  alignSelf: "flex-start",
  m: gr.space[2],
  "& img": {
    display: "block",
    height: 72,
    width: 96,
    objectFit: "cover",
    borderRadius: gr.radius.chip,
  },
};
const removeSx = {
  position: "absolute",
  top: `calc(-1 * ${gr.space[2]})`,
  right: `calc(-1 * ${gr.space[2]})`,
  background: gr.color.surface,
};
const sendSx = { color: gr.color.brand };
const editRowSx = {
  display: "flex",
  alignItems: "center",
  gap: gr.space[2],
  fontSize: gr.font.size.xs,
  color: gr.color.muted,
};

export default function CommentComposer({
  value,
  setValue,
  onSubmit,
  loading = false,
  editing = false,
  onCancel,
  placeholder = "Write a comment…",
  imageSrc = "",
  onPickImage,
  onRemoveImage,
  autoFocus = false,
}) {
  const { userInfo } = useContentSetting();
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const canSend =
    !loading && (String(value || "").trim() !== "" || Boolean(imageSrc));

  const submit = (event) => {
    event.preventDefault();
    if (!canSend) return;
    onSubmit(event);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey))
      submit(event);
  };

  return (
    <Box component="form" onSubmit={submit} sx={formSx}>
      <Box sx={rowSx}>
        <GrAvatar
          size="sm"
          src={s3baseUrl + userInfo?.profile_image}
          name={grCleanName(
            `${userInfo?.first_name || ""} ${userInfo?.last_name || ""}`
          )}
          sx={avatarSx}
        />
        <GrChatComposer
          variant="soft"
          stacked={String(value || "") !== "" || Boolean(imageSrc)}
          top={
            imageSrc ? (
              <Box sx={previewSx}>
                <img src={imageSrc} alt="" />
                <GrIconButton
                  size="sm"
                  aria-label="Remove photo"
                  onClick={onRemoveImage}
                  sx={removeSx}
                >
                  <GrIcon icon={icons.close} size="sm" />
                </GrIconButton>
              </Box>
            ) : null
          }
          end={
            <>
              <GrEmojiPicker
                size="sm"
                onPick={(emoji) =>
                  setValue(insertAtCursor(inputRef.current, emoji))
                }
              />
              {onPickImage && !imageSrc ? (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) onPickImage(file);
                      event.target.value = "";
                    }}
                  />
                  <Tooltip title="Add photo" placement="top">
                    <GrIconButton
                      variant="ghost"
                      size="sm"
                      aria-label="Add photo"
                      onClick={() => fileRef.current?.click()}
                    >
                      <GrIcon icon={icons.image} />
                    </GrIconButton>
                  </Tooltip>
                </>
              ) : null}
              {loading ? (
                <GrLoader
                  size="sm"
                  inline
                  label={editing ? "Saving" : "Posting"}
                />
              ) : (
                <Tooltip title={editing ? "Save" : "Post"} placement="top">
                  <span>
                    <GrIconButton
                      type="submit"
                      variant="ghost"
                      aria-label={editing ? "Save comment" : "Post comment"}
                      disabled={!canSend}
                      sx={sendSx}
                    >
                      <GrIcon icon={icons.send} />
                    </GrIconButton>
                  </span>
                </Tooltip>
              )}
            </>
          }
        >
          <TextareaAutosize
            ref={inputRef}
            minRows={1}
            // Empty: one line (the auto-height measures the hint, which would wrap on phones).
          maxRows={value ? 6 : 1}
            value={value}
            placeholder={placeholder}
            aria-label={placeholder}
            autoFocus={autoFocus}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={handleKeyDown}
          />
        </GrChatComposer>
      </Box>
      {editing ? (
        <Box sx={editRowSx}>
          Editing your comment
          <GrButton variant="link" size="sm" onClick={onCancel}>
            Cancel
          </GrButton>
        </Box>
      ) : null}
    </Box>
  );
}
